import {getFormattedEnvName, isIde} from "../../../../../../misc/envNameUtils.ts";

const IDE_ONLY_TOOLS = new Set([
    'openFile',
    'createFile',
    'editFile',
    'runTerminalCommand',
    'deleteFile',
    'moveFile',
    'searchCodebase',
    'grepCodebase',
    'viewDirectory',
    'reviewChanges',
    'gatherContext'
]);

const NON_IDE_TOOLS = new Set([
    'createPdf',
    'createDocx'
]);

const TOOL_SETTINGS: Record<string, string> = {
    searchInternet: 'enableSearchInternet',
    generateImages: 'enableGenerateImages',
    createPdf: 'enableCreateDocument',
    createDocx: 'enableCreateDocument',
    runTerminalCommand: 'enableRunTerminalCommand',
    reviewChanges: 'enableReviewChanges'
};

const KNOWN_TOOLS = new Set([
    ...IDE_ONLY_TOOLS,
    ...NON_IDE_TOOLS,
    'searchInternet',
    'generateImages'
]);

const buildUnavailableMessage = (toolName: string, reason: string) => {
    return `Tool call cancelled. You cannot use \`${toolName}\` right now because ${reason}. Continue without this tool.`;
};

export const getToolAvailability = (toolName: string, settingsObjRef: any, environmentObjRef: any) => {
    const environment = environmentObjRef.current.environment ?? 'web';
    const formattedEnvironment = getFormattedEnvName(environment);
    const mode = settingsObjRef.current.mode;

    if (mode === 'chat') {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, 'No Tools mode is active')
        };
    }

    if (!KNOWN_TOOLS.has(toolName)) {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, 'this tool is not available in the current environment')
        };
    }

    if (IDE_ONLY_TOOLS.has(toolName) && !isIde(environment)) {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, `it only works in the IDE environment, and the current environment is ${formattedEnvironment}`)
        };
    }

    if (NON_IDE_TOOLS.has(toolName) && isIde(environment)) {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, 'document creation is not available in the IDE environment')
        };
    }

    if (toolName === 'searchCodebase' && environmentObjRef.current.projectEmbeddingsLastUpdated === null) {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, 'codebase search is not enabled for this project')
        };
    }

    const settingsKey = TOOL_SETTINGS[toolName];
    if (settingsKey && settingsObjRef.current[settingsKey] === false) {
        return {
            canUse: false,
            errorMessage: buildUnavailableMessage(toolName, 'it is disabled in the user settings')
        };
    }

    return {
        canUse: true,
        errorMessage: null
    };
};
